// =============================================
// LODGES DATA - SINGLE SOURCE OF TRUTH
// =============================================
// This file contains all lodge data for the Apex Experience site

export interface Lodge {
  id: string
  name: string
  slug: string
  subtitle: string
  location: string
  heroImage: string
  sectionTitle?: string
  sectionDescription: string
  narrativeText: string
  gallery: string[]
  galleryAlts: string[]
  stats: {
    number: string
    label: string
    description: string
  }[]
  features: {
    title: string
    text: string
  }[]
  activities: {
    adventureId: string
    title: string
    description: string
  }[]
  carouselImages: string[]
  carouselAlts: string[]
  detailsTitle: string
  detailsDescription: string
  guestRooms: string[]
  facilities: string[]
  specialFeatures: string[]
  guestyListingId?: string
  comingSoon?: boolean
  comingSoonLabel?: string
  /**
   * Agotado. La tarjeta SIGUE VISIBLE en todo el sitio, pero no es reservable
   * y se agrupa al final junto a los `comingSoon`.
   *
   * Es un campo aparte de `comingSoon` a propósito: `comingSoon` se usa además
   * como fallback de visibilidad — `isVisible(lodge.id, lodge.comingSoon)` —,
   * así que marcarlo ocultaba el lodge de todo el sitio público.
   * Regla: visibilidad → SOLO `comingSoon`; deshabilitar/orden/badge → ambos.
   */
  soldOut?: boolean
  /** Variante visual del badge. 'gold' = dorado brillante (ej. Sold Out). */
  badgeVariant?: 'gold'
  bookingMode?: 'direct' | 'whatsapp' | 'hidden'
}

export const lodges: Lodge[] = [
  {
    id: 'apex-mountain-penthouse',
    name: 'Apex Mountain Penthouse',
    slug: 'apex-mountain-penthouse',
    subtitle: 'Ski-in/ski-out exclusive mountain apartment in La Parva',
    location: 'La Parva • Chile',
    heroImage: '/images/lodges/apex-mountain-penthouse/IMG_2891.webp',
    guestyListingId: '68facf56022850001311ef96', // Guesty BE API listing ID
    soldOut: true,
    comingSoonLabel: 'Sold Out',
    badgeVariant: 'gold',
    bookingMode: 'direct',
    sectionTitle: 'Immerse Yourself in Mountain Luxury',
    sectionDescription: 'Explore our collection of stunning views, carefully curated spaces, and the breathtaking natural beauty that surrounds Apex Mountain Penthouse. From panoramic Andes vistas to intimate lodge interiors, discover what makes this destination truly exceptional.',
    narrativeText: '',
    gallery: [
      '/images/lodges/apex-mountain-penthouse/apex-mountain-penthouse-carousel/22.webp',
      '/images/lodges/apex-mountain-penthouse/apex-mountain-penthouse-carousel/23.webp',
      '/images/lodges/apex-mountain-penthouse/apex-mountain-penthouse-carousel/1.webp',
      '/images/lodges/apex-mountain-penthouse/apex-mountain-penthouse-carousel/2.webp',
      '/images/lodges/apex-mountain-penthouse/apex-mountain-penthouse-carousel/3.webp',
      '/images/lodges/apex-mountain-penthouse/apex-mountain-penthouse-carousel/4.webp',
      '/images/lodges/apex-mountain-penthouse/apex-mountain-penthouse-carousel/5.webp',
      '/images/lodges/apex-mountain-penthouse/apex-mountain-penthouse-carousel/6.webp',
      '/images/lodges/apex-mountain-penthouse/apex-mountain-penthouse-carousel/7.webp',
      '/images/lodges/apex-mountain-penthouse/apex-mountain-penthouse-carousel/8.webp',
      '/images/lodges/apex-mountain-penthouse/apex-mountain-penthouse-carousel/9.webp',
      '/images/lodges/apex-mountain-penthouse/apex-mountain-penthouse-carousel/10.webp',
      '/images/lodges/apex-mountain-penthouse/apex-mountain-penthouse-carousel/11.webp',
      '/images/lodges/apex-mountain-penthouse/apex-mountain-penthouse-carousel/12.webp',
      '/images/lodges/apex-mountain-penthouse/apex-mountain-penthouse-carousel/13.webp',
      '/images/lodges/apex-mountain-penthouse/apex-mountain-penthouse-carousel/14.webp',
      '/images/lodges/apex-mountain-penthouse/apex-mountain-penthouse-carousel/15.webp',
      '/images/lodges/apex-mountain-penthouse/apex-mountain-penthouse-carousel/16.webp',
      '/images/lodges/apex-mountain-penthouse/apex-mountain-penthouse-carousel/17.webp',
      '/images/lodges/apex-mountain-penthouse/apex-mountain-penthouse-carousel/18.webp',
      '/images/lodges/apex-mountain-penthouse/apex-mountain-penthouse-carousel/19.webp',
      '/images/lodges/apex-mountain-penthouse/apex-mountain-penthouse-carousel/20.webp',
      '/images/lodges/apex-mountain-penthouse/apex-mountain-penthouse-carousel/21.webp',
    ],
    galleryAlts: [
      'Apex Mountain Penthouse La Parva ski experience',
      'Apex Mountain Penthouse La Parva ski experience',
      'Apex Mountain Penthouse La Parva ski experience',
      'Apex Mountain Penthouse La Parva ski experience',
      'Apex Mountain Penthouse La Parva ski experience',
      'Apex Mountain Penthouse La Parva ski experience',
      'Apex Mountain Penthouse La Parva ski experience',
      'Apex Mountain Penthouse La Parva ski experience',
      'Apex Mountain Penthouse La Parva ski experience',
      'Apex Mountain Penthouse La Parva ski experience',
      'Apex Mountain Penthouse La Parva ski experience',
      'Apex Mountain Penthouse La Parva ski experience',
      'Apex Mountain Penthouse La Parva ski experience',
      'Apex Mountain Penthouse La Parva ski experience',
      'Apex Mountain Penthouse La Parva ski experience',
      'Apex Mountain Penthouse La Parva ski experience',
      'Apex Mountain Penthouse La Parva ski experience',
      'Apex Mountain Penthouse La Parva ski experience',
      'Apex Mountain Penthouse La Parva ski experience',
      'Apex Mountain Penthouse La Parva ski experience',
      'Apex Mountain Penthouse La Parva ski experience',
      'Apex Mountain Penthouse La Parva ski experience',
      'Apex Mountain Penthouse La Parva ski experience',
    ],
    stats: [
      {
        number: '2,860m',
        label: 'Elevation',
        description: 'Base altitude in the heart of the Andes',
      },
      {
        number: '7',
        label: 'Guests',
        description: 'Exclusive capacity for intimate experiences',
      },
      {
        number: '0m',
        label: 'To Slopes',
        description: 'when snow conditions allow',
      },
      {
        number: '70+',
        label: 'Ski Runs',
        description: 'interconnected with Valle Nevado',
      },
    ],
    features: [],
    activities: [
      {
        adventureId: 'resort-skiing',
        title: 'Resort Skiing',
        description: 'Experience world-class resort skiing at premier destinations. From the powder-filled slopes of the Andes to exclusive access runs, enjoy the perfect blend of luxury and alpine adventure.',
      },
      {
        adventureId: 'heli-skiing',
        title: 'Heli-skiing',
        description: 'Our heli operations access some of the world\'s most pristine ski terrain. From Iceland to Patagonia, drop into untracked open faces and steeps with skilled guides.',
      },
    ],
    carouselImages: [
      '/images/lodges/apex-mountain-penthouse/apex-mountain-penthouse-carousel/22.webp',
      '/images/lodges/apex-mountain-penthouse/apex-mountain-penthouse-carousel/23.webp',
      '/images/lodges/apex-mountain-penthouse/IMG_0353.webp',
      '/images/lodges/apex-mountain-penthouse/IMG_0354.webp',
      '/images/lodges/apex-mountain-penthouse/IMG_0616.webp',
      '/images/lodges/apex-mountain-penthouse/IMG_0629.webp',
      '/images/lodges/apex-mountain-penthouse/IMG_0633.webp',
      '/images/lodges/apex-mountain-penthouse/IMG_0638.webp',
      '/images/lodges/apex-mountain-penthouse/IMG_0644.webp',
      '/images/lodges/apex-mountain-penthouse/IMG_0647.webp',
      '/images/lodges/apex-mountain-penthouse/IMG_0650.webp',
      '/images/lodges/apex-mountain-penthouse/IMG_0653.webp',
      '/images/lodges/apex-mountain-penthouse/IMG_0654.webp',
      '/images/lodges/apex-mountain-penthouse/IMG_0656.webp',
      '/images/lodges/apex-mountain-penthouse/IMG_0661.webp',
      '/images/lodges/apex-mountain-penthouse/IMG_0664.webp',
      '/images/lodges/apex-mountain-penthouse/IMG_0668.webp',
      '/images/lodges/apex-mountain-penthouse/IMG_2878.webp',
      '/images/lodges/apex-mountain-penthouse/IMG_2881.webp',
      '/images/lodges/apex-mountain-penthouse/IMG_2883.webp',
      '/images/lodges/apex-mountain-penthouse/IMG_2887.webp',
      '/images/lodges/apex-mountain-penthouse/IMG_2888.webp',
      '/images/lodges/apex-mountain-penthouse/IMG_2891.webp',
      '/images/lodges/apex-mountain-penthouse/IMG_2897-1.webp',
    ],
    carouselAlts: [
      'Apex Mountain Penthouse interior view',
      'Apex Mountain Penthouse interior view',
      'Apex Mountain Penthouse La Parva mountain views',
      'Apex Mountain Penthouse La Parva mountain views',
      'Apex Mountain Penthouse La Parva mountain views',
      'Apex Mountain Penthouse La Parva mountain views',
      'Apex Mountain Penthouse La Parva mountain views',
      'Apex Mountain Penthouse La Parva mountain views',
      'Apex Mountain Penthouse La Parva mountain views',
      'Apex Mountain Penthouse La Parva mountain views',
      'Apex Mountain Penthouse La Parva mountain views',
      'Apex Mountain Penthouse La Parva mountain views',
      'Apex Mountain Penthouse La Parva mountain views',
      'Apex Mountain Penthouse La Parva mountain views',
      'Apex Mountain Penthouse La Parva mountain views',
      'Apex Mountain Penthouse La Parva mountain views',
      'Apex Mountain Penthouse La Parva mountain views',
      'Apex Mountain Penthouse La Parva mountain views',
      'Apex Mountain Penthouse La Parva mountain views',
      'Apex Mountain Penthouse La Parva mountain views',
      'Apex Mountain Penthouse La Parva mountain views',
      'Apex Mountain Penthouse La Parva mountain views',
      'Apex Mountain Penthouse La Parva mountain views',
      'Apex Mountain Penthouse La Parva mountain views',
    ],
    detailsTitle: 'A Ski-In/Ski-Out Mountain Retreat in La Parva',
    detailsDescription: 'Our boutique penthouse lodge offers an exclusive mountain experience in one of Chile\'s premier ski destinations. With direct access to the slopes and stunning Andes views, enjoy personalized service in an intimate setting.',
    guestRooms: [
      'Five luxury guest rooms with en suite bathrooms',
      'Master suite with panoramic mountain views',
    ],
    facilities: [
      'Private hot tub with Andes mountain views',
      'Wine cellar featuring premium Chilean vintages',
      'Gear room with skis, avalanche safety equipment, and backcountry touring gear',
      'Living room with fireplace and mountain-view terrace',
    ],
    specialFeatures: [
      'Ski-in/ski-out access to La Parva resort',
      'Private chef for gourmet mountain cuisine',
      'Concierge service for guided adventures and lift tickets',
    ],
  },
  {
    id: 'apex-lakefront-serenity',
    name: 'Apex Lakefront Serenity',
    slug: 'apex-lakefront-serenity',
    subtitle: 'Tranquil lakeside retreat in Chilean Lake District',
    location: 'Lake District • Chile',
    heroImage: '/images/lodges/apex-lakefront-serenity/coming-soon/hero-villarrica-lake-volcano.webp',
    comingSoon: true,
    comingSoonLabel: 'Coming Soon',
    bookingMode: 'hidden',
    sectionDescription: 'Experience the serene beauty of Chile\'s Lake District at Apex Lakefront Serenity. From pristine waters to mountain backdrops, discover a peaceful retreat where nature\'s rhythm guides every moment of your lakeside escape.',
    narrativeText: '',
    gallery: [
      'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=80',
      'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=1920&q=80',
      'https://images.unsplash.com/photo-1519904981063-b0cf448d479e?w=1920&q=80',
      'https://images.unsplash.com/photo-1551582045-6ec9c11d8697?w=1920&q=80',
      'https://images.unsplash.com/photo-1605540436563-5bca919ae766?w=1920&q=80',
      'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=80',
    ],
    galleryAlts: [
      'Apex Lakefront Serenity pristine lakefront with mountain backdrop',
      'Apex Lakefront Serenity private dock with kayaks and water sports equipment',
      'Apex Lakefront Serenity luxury lakeside living room with panoramic windows',
      'Apex Lakefront Serenity master suite with private deck overlooking crystal waters',
      'Apex Lakefront Serenity outdoor fire pit for evening lakeside gatherings',
      'Apex Lakefront Serenity sandy beach with 200 meters of private waterfront',
    ],
    stats: [
      {
        number: '15',
        label: 'Hectares',
        description: 'Private lakefront property',
      },
      {
        number: '12',
        label: 'Guests',
        description: 'Spacious capacity for families and groups',
      },
      {
        number: '200m',
        label: 'Waterfront',
        description: 'Private beach and dock access',
      },
      {
        number: '10+',
        label: 'Activities',
        description: 'Diverse water and land adventures',
      },
    ],
    features: [],
    activities: [
      {
        adventureId: 'fly-fishing',
        title: 'Fly Fishing',
        description: 'Cast into pristine Chilean waters renowned for trophy trout. Expert guides lead you to secret pools and productive runs in stunning mountain settings.',
      },
      {
        adventureId: 'water-skiing',
        title: 'Water Skiing',
        description: 'Experience the thrill of water skiing on pristine lakes. From beginners to experts, enjoy the perfect blend of adrenaline and scenic beauty.',
      },
    ],
    carouselImages: [
      'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=80',
      'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=1920&q=80',
      'https://images.unsplash.com/photo-1519904981063-b0cf448d479e?w=1920&q=80',
    ],
    carouselAlts: [
      'Apex Lakefront Serenity pristine lakefront with mountain backdrop',
      'Apex Lakefront Serenity private dock with kayaks and water sports equipment',
      'Apex Lakefront Serenity luxury lakeside living room with panoramic windows',
    ],
    detailsTitle: 'A Lakeside Haven in Chile\'s Lake District',
    detailsDescription: 'Apex Lakefront Serenity offers an exclusive lakeside experience on 15 hectares of pristine property. With 200 meters of private waterfront and diverse activities, enjoy tranquil nature in comfort.',
    guestRooms: [
      'Six luxury guest rooms with lake views and en suite bathrooms',
      'Master suite with private deck overlooking the water',
    ],
    facilities: [
      'Private dock with kayaks, paddleboards, and rowboats',
      'Fly fishing gear room with equipment and guide service',
      'Lakeside fire pit with seating for evening gatherings',
      'Living room with panoramic lake windows and fireplace',
    ],
    specialFeatures: [
      '200 meters of private lakefront with sandy beach',
      'Water skiing and wakeboarding equipment available',
      'Gourmet lake-to-table cuisine with local ingredients',
    ],
  },
  {
    id: 'apex-parva-cave',
    name: 'Apex Parva Cave',
    slug: 'apex-parva-cave',
    subtitle: 'Exclusive ski-in/ski-out mountain retreat',
    location: 'La Parva • Chile',
    heroImage: '/images/lodges/apex-parva-cave/parva-06.webp',
    sectionTitle: 'Experience Authentic Mountain Living',
    sectionDescription: 'Discover Apex Parva Cave, an exclusive ski-in/ski-out retreat carved into the heart of La Parva. From pristine slopes to cozy mountain interiors, immerse yourself in an authentic Chilean ski experience that blends adventure with refined comfort.',
    narrativeText: '',
    bookingMode: 'direct',
    gallery: [
      '/images/lodges/apex-parva-cave/parva-01.webp',
      '/images/lodges/apex-parva-cave/parva-02.webp',
      '/images/lodges/apex-parva-cave/parva-03.webp',
      '/images/lodges/apex-parva-cave/parva-04.webp',
      '/images/lodges/apex-parva-cave/parva-05.webp',
      '/images/lodges/apex-parva-cave/parva-06.webp',
      '/images/lodges/apex-parva-cave/parva-07.webp',
      '/images/lodges/apex-parva-cave/parva-08.webp',
      '/images/lodges/apex-parva-cave/parva-09.webp',
      '/images/lodges/apex-parva-cave/parva-10.webp',
    ],
    galleryAlts: [
      'Apex Parva Cave mountain lodge exterior at La Parva ski resort',
      'Apex Parva Cave interior living area with alpine ambiance',
      'Apex Parva Cave cozy interior details of the mountain retreat',
      'Apex Parva Cave vertical view of the lodge interior',
      'Apex Parva Cave dining space and mountain lifestyle',
      'Apex Parva Cave living room with warm mountain atmosphere',
      'Apex Parva Cave additional interior view of the retreat',
      'Apex Parva Cave lodge details and natural surroundings',
      'Apex Parva Cave exterior and scenic mountain environment',
      'Apex Parva Cave panoramic views from the ski-in/ski-out lodge',
    ],
    stats: [
      {
        number: '2,700m',
        label: 'Elevation',
        description: 'Base altitude in the Chilean Andes',
      },
      {
        number: '8',
        label: 'Guests',
        description: 'Intimate capacity for exclusive experiences',
      },
      {
        number: '0m',
        label: 'To Slopes',
        description: 'True ski-in/ski-out access',
      },
      {
        number: '70',
        label: 'Ski Runs',
        description: 'Direct access to La Parva terrain',
      },
    ],
    features: [],
    activities: [
      {
        adventureId: 'resort-skiing',
        title: 'Resort Skiing',
        description: 'Experience world-class resort skiing at premier destinations. From the powder-filled slopes of the Andes to exclusive access runs, enjoy the perfect blend of luxury and alpine adventure.',
      },
      {
        adventureId: 'ski-touring',
        title: 'Ski Touring',
        description: 'Explore the backcountry with expert guides. Earn your turns in pristine Andean terrain, accessing hidden valleys and untouched powder far from the crowds.',
      },
    ],
    carouselImages: [
      '/images/lodges/apex-parva-cave/parva-01.webp',
      '/images/lodges/apex-parva-cave/parva-03.webp',
      '/images/lodges/apex-parva-cave/parva-06.webp',
      '/images/lodges/apex-parva-cave/parva-08.webp',
      '/images/lodges/apex-parva-cave/parva-10.webp',
    ],
    carouselAlts: [
      'Apex Parva Cave mountain lodge exterior at La Parva ski resort',
      'Apex Parva Cave cozy interior details of the mountain retreat',
      'Apex Parva Cave living room with warm mountain atmosphere',
      'Apex Parva Cave lodge details and natural surroundings',
      'Apex Parva Cave panoramic views from the ski-in/ski-out lodge',
    ],
    detailsTitle: 'A True Ski-In/Ski-Out Cave Retreat',
    detailsDescription: 'Apex Parva Cave offers an intimate mountain experience in La Parva\'s premier ski terrain. With direct slope access and authentic mountain hospitality, enjoy personalized service in an exclusive alpine setting.',
    guestRooms: [
      '4-bedroom house with 3 bathrooms, ready for up to 10 guests',
      '2 en-suite bedrooms + 2 bedrooms sharing 1 full bathroom',
    ],
    facilities: [
      'Ski storage + on-site ski master for waxing, tuning, and repairs',
      'Cozy living with L-shaped sofa, fireplace, and TV',
      'Expansive mountain-view terrace wrapping around the house',
      'BBQ grill perfect for sunset evenings',
    ],
    specialFeatures: [
      "Direct access to 70+ runs from La Parva's prime location",
      'Main street setting, steps from top restaurants',
      'Private parking right at the house',
    ],
  },
  {
    id: 'apex-yelcho-patagonia',
    name: 'Apex Yelcho Patagonia',
    slug: 'apex-yelcho-patagonia',
    subtitle: 'Remote wilderness lodge on Lago Yelcho',
    location: 'Patagonia • Chile',
    heroImage: '/images/lodges/apex-yelcho-patagonia/yelcho-en-la-patagonia_exterior_hotel_1-1-qtpgs4svvtox39co7ll9fi4dr08v34gfk5agnwmmq0.avif',
    bookingMode: 'whatsapp',
    sectionTitle: 'Embrace Patagonian Wilderness',
    sectionDescription: 'Journey to the edge of civilization at Apex Yelcho Patagonia. Surrounded by pristine wilderness, volcanic peaks, and the vast waters of Lago Yelcho, discover a remote sanctuary where adventure and solitude converge.',
    narrativeText: '',
    gallery: [
      '/images/lodges/apex-yelcho-patagonia/yelcho-en-la-patagonia_exterior_hotel_1-1-qtpgs4svvtox39co7ll9fi4dr08v34gfk5agnwmmq0.avif',
      '/images/lodges/apex-yelcho-patagonia/yelcho-en-la-patagonia_exterior_hotel_2-qtpgs2x7i5mcg1feiks0ailgk8i4nq8yvvzhpcpf2g.avif',
      '/images/lodges/apex-yelcho-patagonia/yelcho-en-la-patagonia_exterior_hotel_4-1-qtpgs03oxnihh7jhz1k4l1b2s2w10mxrvi119itll4.avif',
      '/images/lodges/apex-yelcho-patagonia/hotel_yelcho_en_la_patagonia_chaiten_chile-qtpgs7megbss238kr4t54zerj5uyq7rmkj8x3qig7c.avif',
      '/images/lodges/apex-yelcho-patagonia/cabins_yelcho_en_la_patagonia_chaiten_chile-1024x576.avif',
      '/images/lodges/apex-yelcho-patagonia/yelcho-en-la-patagonia_barra_bar-qtph8z2sbyqv3qw0vdnmlr4owj8lzwbavi2n7dnp8o.avif',
      '/images/lodges/apex-yelcho-patagonia/yelcho-en-la-patagonia_restaurante-1-qtph8w99rgn04x04bufqw9ub4dmict03v446rjrvrc.avif',
    ],
    galleryAlts: [
      'Apex Yelcho Patagonia remote wilderness lodge on pristine Lago Yelcho shores',
      'Apex Yelcho Patagonia exterior with volcanic peaks and Patagonian landscape',
      'Apex Yelcho Patagonia luxury cabins surrounded by untouched wilderness',
      'Apex Yelcho Patagonia main lodge with panoramic lake and mountain views',
      'Apex Yelcho Patagonia private cabins nestled in 250 hectares of nature',
      'Apex Yelcho Patagonia cozy bar with Patagonian wood and mountain charm',
      'Apex Yelcho Patagonia gourmet restaurant with lake views and local cuisine',
    ],
    stats: [
      {
        number: '250',
        label: 'Hectares',
        description: 'Private wilderness property',
      },
      {
        number: '12',
        label: 'Guests',
        description: 'Exclusive capacity in remote Patagonia',
      },
      {
        number: '0km',
        label: 'Roads',
        description: 'Accessible only by helicopter or boat',
      },
      {
        number: '100+',
        label: 'Peaks',
        description: 'Surrounding volcanic mountains for heli-skiing',
      },
    ],
    features: [],
    activities: [
      {
        adventureId: 'heli-skiing',
        title: 'Heli-skiing',
        description: 'Access pristine Patagonian peaks by helicopter. Drop into untracked powder with expert guides in one of the world\'s most remote ski destinations.',
      },
      {
        adventureId: 'fly-fishing',
        title: 'Fly Fishing',
        description: 'Cast into Lago Yelcho\'s crystal waters for trophy rainbow and brown trout. Expert guides lead you to secret spots in stunning wilderness.',
      },
    ],
    carouselImages: [
      '/images/lodges/apex-yelcho-patagonia/yelcho-en-la-patagonia_exterior_hotel_1-1-qtpgs4svvtox39co7ll9fi4dr08v34gfk5agnwmmq0.avif',
      '/images/lodges/apex-yelcho-patagonia/yelcho-en-la-patagonia_exterior_hotel_2-qtpgs2x7i5mcg1feiks0ailgk8i4nq8yvvzhpcpf2g.avif',
      '/images/lodges/apex-yelcho-patagonia/yelcho-en-la-patagonia_exterior_hotel_4-1-qtpgs03oxnihh7jhz1k4l1b2s2w10mxrvi119itll4.avif',
      '/images/lodges/apex-yelcho-patagonia/hotel_yelcho_en_la_patagonia_chaiten_chile-qtpgs7megbss238kr4t54zerj5uyq7rmkj8x3qig7c.avif',
      '/images/lodges/apex-yelcho-patagonia/cabins_yelcho_en_la_patagonia_chaiten_chile-1024x576.avif',
      '/images/lodges/apex-yelcho-patagonia/yelcho-en-la-patagonia_barra_bar-qtph8z2sbyqv3qw0vdnmlr4owj8lzwbavi2n7dnp8o.avif',
      '/images/lodges/apex-yelcho-patagonia/yelcho-en-la-patagonia_restaurante-1-qtph8w99rgn04x04bufqw9ub4dmict03v446rjrvrc.avif',
    ],
    carouselAlts: [
      'Apex Yelcho Patagonia remote wilderness lodge on pristine Lago Yelcho shores',
      'Apex Yelcho Patagonia exterior with volcanic peaks and Patagonian landscape',
      'Apex Yelcho Patagonia luxury cabins surrounded by untouched wilderness',
      'Apex Yelcho Patagonia main lodge with panoramic lake and mountain views',
      'Apex Yelcho Patagonia private cabins nestled in 250 hectares of nature',
      'Apex Yelcho Patagonia cozy bar with Patagonian wood and mountain charm',
      'Apex Yelcho Patagonia gourmet restaurant with lake views and local cuisine',
    ],
    detailsTitle: 'A Remote Wilderness Lodge in Patagonia',
    detailsDescription: 'Apex Yelcho Patagonia offers an exclusive wilderness experience on 250 hectares of pristine property. With helicopter access, world-class fishing, and untouched nature, immerse yourself in Patagonian adventure.',
    guestRooms: [
      'Six luxury guest rooms with lake views and en suite bathrooms',
      'Master suite with private deck overlooking Lago Yelcho',
    ],
    facilities: [
      'Private helicopter for heli-skiing and scenic flights',
      'Fly fishing gear room with premium equipment',
      'Lakeside hot tubs and sauna with Patagonian views',
      'Living room with fireplace and panoramic lake windows',
    ],
    specialFeatures: [
      'Accessible only by helicopter or boat for ultimate privacy',
      'World-class heli-skiing in volcanic Patagonian peaks',
      'Trophy trout fishing on pristine Lago Yelcho',
    ],
  },
  {
    id: 'apex-patagonia-lodge',
    name: 'Apex Patagonia Lodge',
    slug: 'apex-patagonia-lodge',
    subtitle: 'Premier fly fishing lodge on pristine Chilean rivers',
    location: 'Patagonian Rivers • Chile',
    heroImage: '/images/lodges/apex-patagonia-lodge/apex-patagonia-lodge-hero.webp',
    soldOut: true,
    comingSoonLabel: 'Sold Out',
    badgeVariant: 'gold',
    bookingMode: 'hidden',
    sectionTitle: 'Discover Fly Fishing Paradise',
    sectionDescription: 'Experience Chile\'s premier fly fishing destination at Apex Patagonia Lodge. From crystal-clear rivers to expert guides, discover why this riverside sanctuary has become legendary among anglers worldwide.',
    narrativeText: '',
    gallery: [
      '/images/lodges/apex-patagonia-lodge/apex-patagonia-lodge-carousel/FullSizeRender.webp',
      '/images/lodges/apex-patagonia-lodge/apex-patagonia-lodge-carousel/IMG_0198.webp',
      '/images/lodges/apex-patagonia-lodge/apex-patagonia-lodge-carousel/IMG_0351.webp',
      '/images/lodges/apex-patagonia-lodge/apex-patagonia-lodge-carousel/IMG_0544.webp',
      '/images/lodges/apex-patagonia-lodge/apex-patagonia-lodge-carousel/IMG_0579.webp',
      '/images/lodges/apex-patagonia-lodge/apex-patagonia-lodge-carousel/IMG_0714.webp',
      '/images/lodges/apex-patagonia-lodge/apex-patagonia-lodge-carousel/IMG_0768.webp',
      '/images/lodges/apex-patagonia-lodge/apex-patagonia-lodge-carousel/IMG_0824.webp',
      '/images/lodges/apex-patagonia-lodge/apex-patagonia-lodge-carousel/IMG_0952.webp',
      '/images/lodges/apex-patagonia-lodge/apex-patagonia-lodge-carousel/IMG_0987.webp',
      '/images/lodges/apex-patagonia-lodge/apex-patagonia-lodge-carousel/IMG_1044.webp',
      '/images/lodges/apex-patagonia-lodge/apex-patagonia-lodge-carousel/IMG_1075.webp',
      '/images/lodges/apex-patagonia-lodge/apex-patagonia-lodge-carousel/IMG_1190.webp',
      '/images/lodges/apex-patagonia-lodge/apex-patagonia-lodge-carousel/IMG_1193.webp',
      '/images/lodges/apex-patagonia-lodge/apex-patagonia-lodge-carousel/IMG_1297.webp',
      '/images/lodges/apex-patagonia-lodge/apex-patagonia-lodge-carousel/IMG_1312.webp',
      '/images/lodges/apex-patagonia-lodge/apex-patagonia-lodge-carousel/IMG_1475.webp',
      '/images/lodges/apex-patagonia-lodge/apex-patagonia-lodge-carousel/IMG_1646.webp',
      '/images/lodges/apex-patagonia-lodge/apex-patagonia-lodge-carousel/IMG_1665.webp',
      '/images/lodges/apex-patagonia-lodge/apex-patagonia-lodge-carousel/IMG_1682.webp',
      '/images/lodges/apex-patagonia-lodge/apex-patagonia-lodge-carousel/IMG_1734.webp',
      '/images/lodges/apex-patagonia-lodge/apex-patagonia-lodge-carousel/IMG_1766.webp',
      '/images/lodges/apex-patagonia-lodge/apex-patagonia-lodge-carousel/IMG_1910.webp',
      '/images/lodges/apex-patagonia-lodge/apex-patagonia-lodge-carousel/IMG_1929.webp',
      '/images/lodges/apex-patagonia-lodge/apex-patagonia-lodge-carousel/IMG_1938.webp',
      '/images/lodges/apex-patagonia-lodge/apex-patagonia-lodge-carousel/IMG_2007.webp',
      '/images/lodges/apex-patagonia-lodge/apex-patagonia-lodge-carousel/IMG_2053.webp',
      '/images/lodges/apex-patagonia-lodge/apex-patagonia-lodge-carousel/IMG_2085.webp',
      '/images/lodges/apex-patagonia-lodge/apex-patagonia-lodge-carousel/IMG_2130.webp',
      '/images/lodges/apex-patagonia-lodge/apex-patagonia-lodge-carousel/IMG_2140.webp',
    ],
    galleryAlts: [
      'Apex Patagonia Lodge fly fishing experience',
      'Apex Patagonia Lodge fly fishing experience',
      'Apex Patagonia Lodge fly fishing experience',
      'Apex Patagonia Lodge fly fishing experience',
      'Apex Patagonia Lodge fly fishing experience',
      'Apex Patagonia Lodge fly fishing experience',
      'Apex Patagonia Lodge fly fishing experience',
      'Apex Patagonia Lodge fly fishing experience',
      'Apex Patagonia Lodge fly fishing experience',
      'Apex Patagonia Lodge fly fishing experience',
      'Apex Patagonia Lodge fly fishing experience',
      'Apex Patagonia Lodge fly fishing experience',
      'Apex Patagonia Lodge fly fishing experience',
      'Apex Patagonia Lodge fly fishing experience',
      'Apex Patagonia Lodge fly fishing experience',
      'Apex Patagonia Lodge fly fishing experience',
      'Apex Patagonia Lodge fly fishing experience',
      'Apex Patagonia Lodge fly fishing experience',
      'Apex Patagonia Lodge fly fishing experience',
      'Apex Patagonia Lodge fly fishing experience',
      'Apex Patagonia Lodge fly fishing experience',
      'Apex Patagonia Lodge fly fishing experience',
      'Apex Patagonia Lodge fly fishing experience',
      'Apex Patagonia Lodge fly fishing experience',
      'Apex Patagonia Lodge fly fishing experience',
      'Apex Patagonia Lodge fly fishing experience',
      'Apex Patagonia Lodge fly fishing experience',
      'Apex Patagonia Lodge fly fishing experience',
      'Apex Patagonia Lodge fly fishing experience',
      'Apex Patagonia Lodge fly fishing experience',
    ],
    stats: [
      {
        number: '12km',
        label: 'Private River',
        description: 'Exclusive access to pristine beats',
      },
      {
        number: '10',
        label: 'Guests',
        description: 'Intimate capacity for focused fishing',
      },
      {
        number: '8+',
        label: 'Rivers',
        description: 'Access to diverse Patagonian waters',
      },
      {
        number: '20+',
        label: 'Avg Size',
        description: 'Trophy rainbow and brown trout (inches)',
      },
    ],
    features: [],
    activities: [
      {
        adventureId: 'fly-fishing',
        title: 'Fly Fishing',
        description: 'Cast into Chile\'s most productive rivers for trophy trout. Expert guides lead you to private beats and secret pools in pristine Patagonian waters.',
      },
      {
        adventureId: 'water-skiing',
        title: 'Water Skiing',
        description: 'Enjoy water skiing on nearby lakes between fishing sessions. A perfect blend of adrenaline and relaxation.',
      },
    ],
    carouselImages: [
      '/images/lodges/apex-patagonia-lodge/Copia-de-6Las-Gualas--768x576.avif',
      '/images/lodges/apex-patagonia-lodge/Copia-de-Suite-Chinook-Chinook-2.Suite-Chinook--768x513.avif',
      '/images/lodges/apex-patagonia-lodge/Copia-de-Suite-Chinook-IMG_0688Suite-Chinook--768x513.avif',
      '/images/lodges/apex-patagonia-lodge/Copia-de-Suite-Chinook-IMG_2546Suite-Chinook--768x513.avif',
      '/images/lodges/apex-patagonia-lodge/Copia-de-SUITE-FARIO-768x432.avif',
      '/images/lodges/apex-patagonia-lodge/Copia-de-Suite-las-gualas-768x576.avif',
      '/images/lodges/apex-patagonia-lodge/Copia-de-Suite-Tagua-Tagua-768x432.avif',
    ],
    carouselAlts: [
      'Apex Patagonia Lodge riverside lodge',
      'Apex Patagonia Lodge Chinook suite',
      'Apex Patagonia Lodge suite interior',
      'Apex Patagonia Lodge premium suite',
      'Apex Patagonia Lodge Fario suite',
      'Apex Patagonia Lodge Las Gualas suite',
      'Apex Patagonia Lodge Tagua Tagua suite',
    ],
    detailsTitle: 'A Premier Fly Fishing Lodge in Patagonia',
    detailsDescription: 'Apex Patagonia Lodge offers an exclusive fly fishing experience with 12km of private river access. With expert guides and world-class trout, discover why this lodge is legendary among anglers.',
    guestRooms: [
      'Five luxury guest rooms with river views and en suite bathrooms',
      'Master suite with private deck overlooking the river',
    ],
    facilities: [
      'Fly tying room with materials and expert instruction',
      'Premium fly fishing gear room with top-tier equipment',
      'Riverside fire pit with evening asado dining',
      'Living room with fireplace and panoramic river views',
    ],
    specialFeatures: [
      '12km of exclusive private river access',
      'Expert guides with intimate local knowledge',
      'Trophy rainbow and brown trout averaging 20+ inches',
    ],
  },
  {
    id: 'apex-premium-lodge',
    name: 'Apex Premium Lodge',
    slug: 'apex-premium-lodge',
    subtitle: 'Ultra-luxury all-inclusive mountain retreat',
    location: 'Chilean Andes • Chile',
    heroImage: '/images/lodges/apex-premium-lodge/coming-soon/hero-farellones-panoramic.webp',
    comingSoon: true,
    comingSoonLabel: 'Snow Season 2027',
    bookingMode: 'hidden',
    sectionTitle: 'Experience Ultimate Luxury',
    sectionDescription: 'Discover Apex Premium Lodge, where every detail has been perfected. From helicopter arrivals to private butler service, experience the pinnacle of mountain luxury in the Chilean Andes.',
    narrativeText: '',
    gallery: [
      'https://images.unsplash.com/photo-1605540436563-5bca919ae766?w=1920&q=80',
      '/images/ski-in-ski-out-slopes-access.webp',
      'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=1920&q=80',
      'https://images.unsplash.com/photo-1519904981063-b0cf448d479e?w=1920&q=80',
      'https://images.unsplash.com/photo-1551582045-6ec9c11d8697?w=1920&q=80',
      'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=80',
    ],
    galleryAlts: [
      'Apex Premium Lodge ultra-luxury summit retreat at 3000m elevation',
      'Apex Premium Lodge helicopter pad with direct transfers and heli-skiing access',
      'Apex Premium Lodge presidential suite with private spa and butler service',
      'Apex Premium Lodge world-class spa with massage, sauna, and wellness treatments',
      'Apex Premium Lodge gourmet dining with sommelier and premium Chilean wines',
      'Apex Premium Lodge panoramic Andes terrace with infinity mountain views',
    ],
    stats: [
      {
        number: '3,000m',
        label: 'Elevation',
        description: 'Summit location in the Andes',
      },
      {
        number: '16',
        label: 'Guests',
        description: 'Spacious capacity for luxury groups',
      },
      {
        number: '1:2',
        label: 'Staff Ratio',
        description: 'Dedicated service for every need',
      },
      {
        number: '5-Star',
        label: 'Rating',
        description: 'Ultra-luxury all-inclusive experience',
      },
    ],
    features: [],
    activities: [
      {
        adventureId: 'heli-skiing',
        title: 'Heli-skiing',
        description: 'Private helicopter access to exclusive Andean peaks. Experience untracked powder with world-class guides.',
      },
      {
        adventureId: 'resort-skiing',
        title: 'Resort Skiing',
        description: 'VIP access to Chile\'s premier ski resorts with private instructors and priority lift access.',
      },
    ],
    carouselImages: [
      '/images/ski-in-ski-out-slopes-access.webp',
      '/images/apex-luxury-living-room-interior.webp',
      '/images/apex-outdoor-hot-tub-mountain-rock.webp',
    ],
    carouselAlts: [
      'Apex Premium Lodge helicopter pad with direct transfers and heli-skiing access',
      'Apex Premium Lodge presidential suite with private spa and butler service',
      'Apex Premium Lodge world-class spa with massage, sauna, and wellness treatments',
    ],
    detailsTitle: 'An Ultra-Luxury All-Inclusive Mountain Retreat',
    detailsDescription: 'Apex Premium Lodge offers the pinnacle of mountain luxury at 3,000 meters in the Chilean Andes. With helicopter transfers, butler service, and world-class amenities, experience the ultimate all-inclusive retreat.',
    guestRooms: [
      'Eight luxury suites with panoramic Andes views and en suite bathrooms',
      'Presidential suite with private spa, butler service, and helicopter pad access',
    ],
    facilities: [
      'Private helicopter pad for direct transfers and heli-skiing',
      'World-class spa with massage, sauna, and wellness treatments',
      'Wine cellar with sommelier and premium Chilean vintages',
      'Living areas with fireplaces, cinema room, and panoramic terraces',
    ],
    specialFeatures: [
      'Private butler service for personalized attention',
      'Helicopter transfers from Santiago included',
      'All-inclusive luxury with gourmet cuisine and premium beverages',
    ],
  },
]

export function getLodgeBySlug(slug: string): Lodge | undefined {
  return lodges.find((lodge) => lodge.slug === slug)
}

export function getLodgeById(id: string): Lodge | undefined {
  return lodges.find((lodge) => lodge.id === id)
}

export function getLodgesForActivity(activityId: string): Lodge[] {
  const matches = lodges.filter(lodge =>
    lodge.activities.some(a => a.adventureId === activityId)
  )
  // Bookable lodges first, then the non-bookable ones (comingSoon + soldOut)
  const isNotBookable = (lodge: Lodge): boolean => !!lodge.comingSoon || !!lodge.soldOut
  return matches.sort((a, b) => {
    if (isNotBookable(a) && !isNotBookable(b)) return 1
    if (!isNotBookable(a) && isNotBookable(b)) return -1
    return 0
  })
}
